import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { usePortvilleRooms } from '../../../hooks/admin/usePortvilleRooms';
import usePortvilleRoomAuth from '../../../hooks/admin/usePortvilleRoomAuth';
import { useAuth } from '../../../hooks/useAuth';
import RoomForm from '../../../components/admin/portville-rooms/RoomForm';
import { PortvilleRoomWithResidency, PortvilleRoomFormData } from '../../../types/portvilleRoom';
import { MoveOutService } from '../../../services/MoveOutService';
import { setAdminScrollTop } from '../../../utils/adminScroll';
import { useBackToPortvilleRoomsListOnPop } from '../../../hooks/useBackToPortvilleRoomsListOnPop';
import { markPortvilleRoomsRestoreOnNext } from '../../../utils/portvilleRoomsScroll';

const RoomEditPage: React.FC = () => {
  const { roomId } = useParams<{ roomId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { loading: authLoading, hasAccess } = usePortvilleRoomAuth();
  const { allRooms, updateRoom, loading } = usePortvilleRooms();
  const [room, setRoom] = useState<PortvilleRoomWithResidency | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [processingMoveOut, setProcessingMoveOut] = useState(false);

  useBackToPortvilleRoomsListOnPop();

  useEffect(() => {
    setAdminScrollTop(0);
  }, []);

  useEffect(() => {
    if (!authLoading && (!user || !hasAccess)) {
      navigate('/admin/dashboard');
      return;
    }
  }, [authLoading, user, hasAccess, navigate]);

  useEffect(() => {
    if (roomId && allRooms.length > 0) {
      const foundRoom = allRooms.find(r => r.id === roomId);
      setRoom(foundRoom || null);
    }
  }, [roomId, allRooms]);

  const handleSubmit = async (formData: PortvilleRoomFormData): Promise<boolean> => {
    if (!room) return false;

    setIsSubmitting(true);
    try {
      const success = await updateRoom(room.id, formData);
      if (success) {
        alert('호실 정보가 성공적으로 수정되었습니다.');
        markPortvilleRoomsRestoreOnNext();
        navigate('/admin/portville-rooms', { state: { restoreScroll: true } });
        return true;
      }
      return false;
    } catch (error) {
      console.error('호실 수정 실패:', error);
      alert('호실 수정 중 오류가 발생했습니다.');
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    markPortvilleRoomsRestoreOnNext();
    navigate('/admin/portville-rooms', { state: { restoreScroll: true } });
  };

  const handleViewContract = (
    room: PortvilleRoomWithResidency,
    contractType?: 'original' | 'extension1' | 'extension2'
  ) => {
    navigate(`/admin/portville-rooms/view-contract/${room.id}?type=${contractType || 'original'}`);
  };

  const handleMoveOutAction = async (action: 'detail' | 'approve' | 'reject', requestId: string) => {
    if (!user || !room) return;

    if (action === 'detail') {
      // 상세보기 모달 표시 로직 (필요시 구현)
      return;
    }

    if (action === 'approve') {
      const reason = prompt('승인 사유를 입력하세요:');
      if (!reason) return;

      setProcessingMoveOut(true);
      try {
        await MoveOutService.approveMoveOutRequest(requestId, user.uid, reason);
        alert('퇴거신청이 승인되었습니다.');
        // 페이지 새로고침을 통해 데이터 업데이트
        window.location.reload();
      } catch (error) {
        console.error('퇴거신청 승인 실패:', error);
        alert('퇴거신청 승인에 실패했습니다.');
      } finally {
        setProcessingMoveOut(false);
      }
    }

    if (action === 'reject') {
      const reason = prompt('반려 사유를 입력하세요:');
      if (!reason) return;

      setProcessingMoveOut(true);
      try {
        await MoveOutService.rejectMoveOutRequest(requestId, user.uid, reason);
        alert('퇴거신청이 반려되었습니다.');
        // 페이지 새로고침을 통해 데이터 업데이트
        window.location.reload();
      } catch (error) {
        console.error('퇴거신청 반려 실패:', error);
        alert('퇴거신청 반려에 실패했습니다.');
      } finally {
        setProcessingMoveOut(false);
      }
    }
  };

  if (authLoading || loading) {
    return (
      <div className="w-full min-h-screen bg-[#f7f9fd]">
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#155acb]"></div>
        </div>
      </div>
    );
  }

  if (!room) {
    return (
      <div className="w-full min-h-screen bg-[#f7f9fd]">
        <div className="max-w-[1600px] mx-auto relative pt-8 px-6">
          <div className="mt-8">
            <div className="flex items-center justify-between">
              <div className="text-slate-800 text-4xl font-extrabold font-['Pretendard'] leading-9">
                호실 수정
              </div>
              <button
                onClick={handleCancel}
                className="w-[130px] h-11 bg-[#6b7684] text-white text-base font-bold font-['Pretendard'] rounded-sm hover:bg-[#6b7684]/90 transition-colors"
              >
                목록으로
              </button>
            </div>
            <div className="w-full h-px mt-8 outline outline-1 outline-offset-[-0.50px] outline-neutral-200"></div>
          </div>

          <div className="mt-8 bg-white rounded-sm outline outline-1 outline-slate-200 p-8">
            <div className="text-center text-gray-500">
              해당 호실을 찾을 수 없습니다.
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full min-h-screen bg-[#f7f9fd]">
      <div className="max-w-[1600px] mx-auto relative pt-8 px-6">
        {/* 헤더 섹션 */}
        <div className="mt-8">
          <div className="flex items-center justify-between">
            <div className="text-slate-800 text-4xl font-extrabold font-['Pretendard'] leading-9">
              호실 수정
            </div>
            <button
              onClick={handleCancel}
              className="w-[130px] h-11 bg-[#6b7684] text-white text-base font-bold font-['Pretendard'] rounded-sm hover:bg-[#6b7684]/90 transition-colors"
            >
              목록으로
            </button>
          </div>
          <div className="w-full h-px mt-8 outline outline-1 outline-offset-[-0.50px] outline-neutral-200"></div>
        </div>

        {/* 폼 카드 */}
        <div className="mt-8 bg-white rounded-sm outline outline-1 outline-slate-200">
          <div className="p-8">
            <h2 className="text-[#1e2c49] text-xl font-bold font-['Pretendard'] leading-9 mb-4">
              호실 정보 수정
            </h2>
            <div className="w-[calc(100%-64px)] h-px outline outline-1 outline-offset-[-0.50px] outline-[#e5e8eb] mb-6"></div>

            <RoomForm
              room={room}
              onSubmit={handleSubmit}
              onCancel={handleCancel}
              onViewContract={handleViewContract}
              onMoveOutAction={handleMoveOutAction}
              isSubmitting={isSubmitting}
              processingMoveOut={processingMoveOut}
            />
          </div>
        </div>

        {/* 하단 여백 */}
        <div className="h-16"></div>
      </div>
    </div>
  );
};

export default RoomEditPage;
